import { useEffect, useRef, useState } from 'react'

import type { RefObject } from 'react'

const DEFAULT_SELECTION_HEIGHT = 40

type DropdownPosition = 'top' | 'bottom'

interface UseDropdownPositionReturn<RefOne extends HTMLElement, RefTwo extends HTMLElement> {
  dropdownPosition: DropdownPosition
  containerRef: RefObject<RefOne | null>
  selectionRef: RefObject<RefTwo | null>
}

export const useDropdownPosition = <RefOne extends HTMLElement = HTMLElement, RefTwo extends HTMLElement = HTMLElement>(
  dataLength: number,
  status: string,
): UseDropdownPositionReturn<RefOne, RefTwo> => {
  const containerRef = useRef<RefOne>(null)
  const selectionRef = useRef<RefTwo>(null)
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>('bottom')

  useEffect(() => {
    if (!containerRef.current || status !== 'OK') return

    const calculatePosition = () => {
      const inputRect = containerRef.current?.getBoundingClientRect()
      if (!inputRect) return

      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - inputRect.bottom
      const itemHeight = selectionRef.current?.clientHeight || DEFAULT_SELECTION_HEIGHT
      const dropdownHeight = dataLength * itemHeight

      if (spaceBelow < dropdownHeight && inputRect.top > dropdownHeight) {
        setDropdownPosition('top')
      } else {
        setDropdownPosition('bottom')
      }
    }

    calculatePosition()
    window.addEventListener('resize', calculatePosition)

    return () => {
      window.removeEventListener('resize', calculatePosition)
    }
  }, [dataLength, status])

  return {
    dropdownPosition,
    containerRef,
    selectionRef,
  }
}
